import { useTranslation } from "react-i18next";
import { CourseData } from "@packages/constant/CourseData";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import { useState, useEffect } from "react";
import {
  Book,
  GraduationCap,
  BookOpen,
  Milestone,
  Dna,
  FlaskConical,
  Zap,
  Waves,
  Magnet,
  Gauge,
  TrendingUp,
  BarChart3,
  FunctionSquare,
  Code,
  Network,
  Brain,
  DollarSign,
  Coins,
  Earth,
  Castle,
  Globe,
  Map,
  Building,
  Microscope,
  Atom,
  Calculator,
  Languages,
  Palette,
  Music,
  Drama,
  Briefcase,
  Landmark,
  TreePine,
  Compass,
  Scale,
  Users,
  Wrench,
  Lightbulb,
  FileText,
  Camera,
  Cpu,
  Database,
  Rocket,
  Activity,
  Trophy,
  Star,
  Crown,
  Diamond,
} from "lucide-react";

// Import TSX icon components
import BiologyIcon from "@packages/assets/highschool/biyoloji.tsx";
import GeographyIcon from "@packages/assets/highschool/cografya.tsx";
import LiteratureIcon from "@packages/assets/highschool/edebiyat.tsx";
import PhilosophyIcon from "@packages/assets/highschool/felsefe.tsx";
import PhysicsIcon from "@packages/assets/highschool/fizik.tsx";
import ChemistryIcon from "@packages/assets/highschool/kimya.tsx";
import MathematicsIcon from "@packages/assets/highschool/math.tsx";
import HistoryIcon from "@packages/assets/highschool/tarih.tsx";

interface CourseValue {
  name: (grade: number) => string;
  visible: (grade: number) => boolean;
  hasOutcomes: boolean;
}

type CourseEntry = [string, CourseValue];

type IconComponent = React.ComponentType<any>;

// Course icons mapping with comprehensive coverage
const courseIcons: Record<string, IconComponent> = {
  BIOLOGY: BiologyIcon,
  GEOGRAPHY: GeographyIcon,
  TURKISH: LiteratureIcon,
  PHILOSOPHY: PhilosophyIcon,
  PHYSICS: PhysicsIcon,
  CHEMISTRY: ChemistryIcon,
  MATHEMATICS: MathematicsIcon,
  HISTORY: HistoryIcon,
  DIGITAL_CITIZENSHIP: BookOpen,
  ENGLISH: Book,
  SCIENCE: ChemistryIcon,
  RELIGION: BookOpen,
  CITIZENSHIP: Milestone,

  // AP Course Icons
  AP_2025_BIOLOGY: Dna,
  AP_2025_CHEMISTRY: FlaskConical,
  AP_2025_PHYSICS_1: Zap,
  AP_2025_PHYSICS_2: Waves,
  AP_2025_PHYSICS_C_ELECTRICITY_AND_MAGNETISM: Magnet,
  AP_2025_PHYSICS_C_MECHANICS: Gauge,
  AP_2025_CALCULUS_AB_AND_BC: TrendingUp,
  AP_2025_PRECALCULUS: FunctionSquare,
  AP_2025_STATISTICS: BarChart3,
  AP_2025_COMPUTER_SCIENCE_A: Code,
  AP_2025_COMPUTER_SCIENCE_PRINCIPLES: Network,
  AP_2025_PSYCHOLOGY: Brain,
  AP_2025_MACROECONOMICS: DollarSign,
  AP_2025_MICROECONOMICS: Coins,
  AP_2025_ENVIRONMENTAL_SCIENCE: Earth,
  AP_2025_EUROPEAN_HISTORY: Castle,
  AP_2025_WORLD_HISTORY_MODERN: Globe,
  AP_2025_HUMAN_GEOGRAPHY: Map,
  AP_2025_COMPARATIVE_GOVERNMENT_AND_POLITICS: Building,

  // IGCSE Course Icons
  IGCSE_BIOLOGY: Microscope,
  IGCSE_CHEMISTRY: Atom,
  IGCSE_PHYSICS: Lightbulb,
  IGCSE_MATHEMATICS: Calculator,
  IGCSE_ENGLISH: Languages,
  IGCSE_LITERATURE: BookOpen,
  IGCSE_HISTORY: Landmark,
  IGCSE_GEOGRAPHY: Compass,
  IGCSE_ECONOMICS: Briefcase,
  IGCSE_BUSINESS_STUDIES: TrendingUp,
  IGCSE_ACCOUNTING: BarChart3,
  IGCSE_COMPUTER_SCIENCE: Cpu,
  IGCSE_ICT: Database,
  IGCSE_ART_AND_DESIGN: Palette,
  IGCSE_MUSIC: Music,
  IGCSE_DRAMA: Drama,
  IGCSE_PHYSICAL_EDUCATION: Activity,
  IGCSE_ENVIRONMENTAL_MANAGEMENT: TreePine,
  IGCSE_PSYCHOLOGY: Brain,
  IGCSE_SOCIOLOGY: Users,

  // IB Course Icons
  IB_DP_BIOLOGY: Dna,
  IB_DP_CHEMISTRY: FlaskConical,
  IB_DP_PHYSICS: Rocket,
  IB_DP_MATHEMATICS: FunctionSquare,
  IB_DP_ENGLISH: Book,
  IB_DP_LITERATURE: FileText,
  IB_DP_HISTORY: HistoryIcon,
  IB_DP_GEOGRAPHY: Globe,
  IB_DP_ECONOMICS: DollarSign,
  IB_DP_BUSINESS_MANAGEMENT: Briefcase,
  IB_DP_PSYCHOLOGY: Brain,
  IB_DP_COMPUTER_SCIENCE: Code,
  IB_DP_VISUAL_ARTS: Palette,
  IB_DP_MUSIC: Music,
  IB_DP_THEATRE: Drama,
  IB_DP_PHILOSOPHY: PhilosophyIcon,
  IB_DP_ENVIRONMENTAL_SYSTEMS: Earth,
  IB_DP_SPORTS_SCIENCE: Trophy,
  IB_DP_DESIGN_TECHNOLOGY: Wrench,
  IB_DP_THEORY_OF_KNOWLEDGE: Lightbulb,

  // A-Level Course Icons
  ALEVEL_BIOLOGY: Microscope,
  ALEVEL_CHEMISTRY: Atom,
  ALEVEL_PHYSICS: Zap,
  ALEVEL_MATHEMATICS: Calculator,
  ALEVEL_FURTHER_MATHEMATICS: TrendingUp,
  ALEVEL_ENGLISH_LITERATURE: BookOpen,
  ALEVEL_ENGLISH_LANGUAGE: Languages,
  ALEVEL_HISTORY: Castle,
  ALEVEL_GEOGRAPHY: Map,
  ALEVEL_ECONOMICS: Coins,
  ALEVEL_BUSINESS_STUDIES: Building,
  ALEVEL_ACCOUNTING: BarChart3,
  ALEVEL_COMPUTER_SCIENCE: Network,
  ALEVEL_ART_AND_DESIGN: Camera,
  ALEVEL_MUSIC: Music,
  ALEVEL_DRAMA: Drama,
  ALEVEL_PSYCHOLOGY: Brain,
  ALEVEL_SOCIOLOGY: Users,
  ALEVEL_PHILOSOPHY: PhilosophyIcon,
  ALEVEL_LAW: Scale,
  ALEVEL_POLITICS: Building,
  ALEVEL_PHYSICAL_EDUCATION: Activity,
  ALEVEL_MEDIA_STUDIES: Camera,
  ALEVEL_DESIGN_TECHNOLOGY: Wrench,

  DEFAULT: Book,
};

// Group icons mapping
const groupIcons: Record<string, IconComponent> = {
  IB: Star,
  IGCSE: Crown,
  ALEVEL: Diamond,
  Other: Book,
};

const courseColors: Record<
  string,
  {
    bg: string;
    activeBg: string;
    border: string;
    activeBorder: string;
    hoverBg: string;
    text: string;
    activeText: string;
  }
> = {
  HISTORY: {
    bg: "bg-amber-50/70 dark:bg-amber-950/20",
    activeBg: "bg-amber-100/80 dark:bg-amber-900/30",
    border: "border-amber-200/60 dark:border-amber-800/40",
    activeBorder: "border-amber-300 dark:border-amber-600",
    hoverBg: "hover:bg-amber-75/60 dark:hover:bg-amber-900/25",
    text: "text-amber-700 dark:text-amber-300",
    activeText: "text-amber-900 dark:text-amber-100",
  },
  GEOGRAPHY: {
    bg: "bg-sky-50/70 dark:bg-sky-950/20",
    activeBg: "bg-sky-100/80 dark:bg-sky-900/30",
    border: "border-sky-200/60 dark:border-sky-800/40",
    activeBorder: "border-sky-300 dark:border-sky-600",
    hoverBg: "hover:bg-sky-75/60 dark:hover:bg-sky-900/25",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  CHEMISTRY: {
    bg: "bg-indigo-50/70 dark:bg-indigo-950/20",
    activeBg: "bg-indigo-100/80 dark:bg-indigo-900/30",
    border: "border-indigo-200/60 dark:border-indigo-800/40",
    activeBorder: "border-indigo-300 dark:border-indigo-600",
    hoverBg: "hover:bg-indigo-75/60 dark:hover:bg-indigo-900/25",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  PHYSICS: {
    bg: "bg-emerald-50/70 dark:bg-emerald-950/20",
    activeBg: "bg-emerald-100/80 dark:bg-emerald-900/30",
    border: "border-emerald-200/60 dark:border-emerald-800/40",
    activeBorder: "border-emerald-300 dark:border-emerald-600",
    hoverBg: "hover:bg-emerald-75/60 dark:hover:bg-emerald-900/25",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  TURKISH: {
    bg: "bg-rose-50/70 dark:bg-rose-950/20",
    activeBg: "bg-rose-100/80 dark:bg-rose-900/30",
    border: "border-rose-200/60 dark:border-rose-800/40",
    activeBorder: "border-rose-300 dark:border-rose-600",
    hoverBg: "hover:bg-rose-75/60 dark:hover:bg-rose-900/25",
    text: "text-rose-700 dark:text-rose-300",
    activeText: "text-rose-900 dark:text-rose-100",
  },
  MATHEMATICS: {
    bg: "bg-orange-50/70 dark:bg-orange-950/20",
    activeBg: "bg-orange-100/80 dark:bg-orange-900/30",
    border: "border-orange-200/60 dark:border-orange-800/40",
    activeBorder: "border-orange-300 dark:border-orange-600",
    hoverBg: "hover:bg-orange-75/60 dark:hover:bg-orange-900/25",
    text: "text-orange-700 dark:text-orange-300",
    activeText: "text-orange-900 dark:text-orange-100",
  },
  BIOLOGY: {
    bg: "bg-teal-50/70 dark:bg-teal-950/20",
    activeBg: "bg-teal-100/80 dark:bg-teal-900/30",
    border: "border-teal-200/60 dark:border-teal-800/40",
    activeBorder: "border-teal-300 dark:border-teal-600",
    hoverBg: "hover:bg-teal-75/60 dark:hover:bg-teal-900/25",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  DIGITAL_CITIZENSHIP: {
    bg: "bg-blue-50/70 dark:bg-blue-950/20",
    activeBg: "bg-blue-100/80 dark:bg-blue-900/30",
    border: "border-blue-200/60 dark:border-blue-800/40",
    activeBorder: "border-blue-300 dark:border-blue-600",
    hoverBg: "hover:bg-blue-75/60 dark:hover:bg-blue-900/25",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  ENGLISH: {
    bg: "bg-purple-50/70 dark:bg-purple-950/20",
    activeBg: "bg-purple-100/80 dark:bg-purple-900/30",
    border: "border-purple-200/60 dark:border-purple-800/40",
    activeBorder: "border-purple-300 dark:border-purple-600",
    hoverBg: "hover:bg-purple-75/60 dark:hover:bg-purple-900/25",
    text: "text-purple-700 dark:text-purple-300",
    activeText: "text-purple-900 dark:text-purple-100",
  },
  SCIENCE: {
    bg: "bg-green-50/70 dark:bg-green-950/20",
    activeBg: "bg-green-100/80 dark:bg-green-900/30",
    border: "border-green-200/60 dark:border-green-800/40",
    activeBorder: "border-green-300 dark:border-green-600",
    hoverBg: "hover:bg-green-75/60 dark:hover:bg-green-900/25",
    text: "text-green-700 dark:text-green-300",
    activeText: "text-green-900 dark:text-green-100",
  },
  PHILOSOPHY: {
    bg: "bg-violet-50/70 dark:bg-violet-950/20",
    activeBg: "bg-violet-100/80 dark:bg-violet-900/30",
    border: "border-violet-200/60 dark:border-violet-800/40",
    activeBorder: "border-violet-300 dark:border-violet-600",
    hoverBg: "hover:bg-violet-75/60 dark:hover:bg-violet-900/25",
    text: "text-violet-700 dark:text-violet-300",
    activeText: "text-violet-900 dark:text-violet-100",
  },
  RELIGION: {
    bg: "bg-slate-50/70 dark:bg-slate-950/20",
    activeBg: "bg-slate-100/80 dark:bg-slate-900/30",
    border: "border-slate-200/60 dark:border-slate-800/40",
    activeBorder: "border-slate-300 dark:border-slate-600",
    hoverBg: "hover:bg-slate-75/60 dark:hover:bg-slate-900/25",
    text: "text-slate-700 dark:text-slate-300",
    activeText: "text-slate-900 dark:text-slate-100",
  },
  CITIZENSHIP: {
    bg: "bg-pink-50/70 dark:bg-pink-950/20",
    activeBg: "bg-pink-100/80 dark:bg-pink-900/30",
    border: "border-pink-200/60 dark:border-pink-800/40",
    activeBorder: "border-pink-300 dark:border-pink-600",
    hoverBg: "hover:bg-pink-75/60 dark:hover:bg-pink-900/25",
    text: "text-pink-700 dark:text-pink-300",
    activeText: "text-pink-900 dark:text-pink-100",
  },

  // Base subjects to unify all variants (AP/IB/IGCSE/ALEVEL) under the same palette
  COMPUTER_SCIENCE: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  BUSINESS_STUDIES: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  SOCIOLOGY: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-purple-50/60 dark:from-indigo-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-purple-100/70 dark:from-indigo-900/35 dark:to-purple-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-purple-75/50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },

  // AP Course Colors - Premium, eye-friendly design with soft gradients
  AP_2025_BIOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/25 dark:to-teal-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-teal-100/70 dark:from-emerald-900/35 dark:to-teal-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-teal-75/50 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  AP_2025_CHEMISTRY: {
    bg: "bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-950/25 dark:to-indigo-950/15",
    activeBg:
      "bg-gradient-to-br from-blue-100/90 to-indigo-100/70 dark:from-blue-900/35 dark:to-indigo-900/25",
    border: "border-blue-200/50 dark:border-blue-700/40",
    activeBorder: "border-blue-400/70 dark:border-blue-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-blue-75/70 hover:to-indigo-75/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/20",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  AP_2025_PHYSICS_1: {
    bg: "bg-gradient-to-br from-cyan-50/80 to-sky-50/60 dark:from-cyan-950/25 dark:to-sky-950/15",
    activeBg:
      "bg-gradient-to-br from-cyan-100/90 to-sky-100/70 dark:from-cyan-900/35 dark:to-sky-900/25",
    border: "border-cyan-200/50 dark:border-cyan-700/40",
    activeBorder: "border-cyan-400/70 dark:border-cyan-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-cyan-75/70 hover:to-sky-75/50 dark:hover:from-cyan-900/30 dark:hover:to-sky-900/20",
    text: "text-cyan-700 dark:text-cyan-300",
    activeText: "text-cyan-900 dark:text-cyan-100",
  },
  AP_2025_PHYSICS_2: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  AP_2025_PHYSICS_C_ELECTRICITY_AND_MAGNETISM: {
    bg: "bg-gradient-to-br from-purple-50/80 to-violet-50/60 dark:from-purple-950/25 dark:to-violet-950/15",
    activeBg:
      "bg-gradient-to-br from-purple-100/90 to-violet-100/70 dark:from-purple-900/35 dark:to-violet-900/25",
    border: "border-purple-200/50 dark:border-purple-700/40",
    activeBorder: "border-purple-400/70 dark:border-purple-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-purple-75/70 hover:to-violet-75/50 dark:hover:from-purple-900/30 dark:hover:to-violet-900/20",
    text: "text-purple-700 dark:text-purple-300",
    activeText: "text-purple-900 dark:text-purple-100",
  },
  AP_2025_PHYSICS_C_MECHANICS: {
    bg: "bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-950/25 dark:to-indigo-950/15",
    activeBg:
      "bg-gradient-to-br from-blue-100/90 to-indigo-100/70 dark:from-blue-900/35 dark:to-indigo-900/25",
    border: "border-blue-200/50 dark:border-blue-700/40",
    activeBorder: "border-blue-400/70 dark:border-blue-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-blue-75/70 hover:to-indigo-75/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/20",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  AP_2025_CALCULUS_AB_AND_BC: {
    bg: "bg-gradient-to-br from-red-50/80 to-rose-50/60 dark:from-red-950/25 dark:to-rose-950/15",
    activeBg:
      "bg-gradient-to-br from-red-100/90 to-rose-100/70 dark:from-red-900/35 dark:to-rose-900/25",
    border: "border-red-200/50 dark:border-red-700/40",
    activeBorder: "border-red-400/70 dark:border-red-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-red-75/70 hover:to-rose-75/50 dark:hover:from-red-900/30 dark:hover:to-rose-900/20",
    text: "text-red-700 dark:text-red-300",
    activeText: "text-red-900 dark:text-red-100",
  },
  AP_2025_PRECALCULUS: {
    bg: "bg-gradient-to-br from-yellow-50/80 to-amber-50/60 dark:from-yellow-950/25 dark:to-amber-950/15",
    activeBg:
      "bg-gradient-to-br from-yellow-100/90 to-amber-100/70 dark:from-yellow-900/35 dark:to-amber-900/25",
    border: "border-yellow-200/50 dark:border-yellow-700/40",
    activeBorder: "border-yellow-400/70 dark:border-yellow-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-yellow-75/70 hover:to-amber-75/50 dark:hover:from-yellow-900/30 dark:hover:to-amber-900/20",
    text: "text-yellow-700 dark:text-yellow-300",
    activeText: "text-yellow-900 dark:text-yellow-100",
  },
  AP_2025_STATISTICS: {
    bg: "bg-gradient-to-br from-pink-50/80 to-rose-50/60 dark:from-pink-950/25 dark:to-rose-950/15",
    activeBg:
      "bg-gradient-to-br from-pink-100/90 to-rose-100/70 dark:from-pink-900/35 dark:to-rose-900/25",
    border: "border-pink-200/50 dark:border-pink-700/40",
    activeBorder: "border-pink-400/70 dark:border-pink-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-pink-75/70 hover:to-rose-75/50 dark:hover:from-pink-900/30 dark:hover:to-rose-900/20",
    text: "text-pink-700 dark:text-pink-300",
    activeText: "text-pink-900 dark:text-pink-100",
  },
  AP_2025_COMPUTER_SCIENCE_A: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  AP_2025_COMPUTER_SCIENCE_PRINCIPLES: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  AP_2025_PSYCHOLOGY: {
    bg: "bg-gradient-to-br from-fuchsia-50/80 to-pink-50/60 dark:from-fuchsia-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-fuchsia-100/90 to-pink-100/70 dark:from-fuchsia-900/35 dark:to-pink-900/25",
    border: "border-fuchsia-200/50 dark:border-fuchsia-700/40",
    activeBorder: "border-fuchsia-400/70 dark:border-fuchsia-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-fuchsia-75/70 hover:to-pink-75/50 dark:hover:from-fuchsia-900/30 dark:hover:to-pink-900/20",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
    activeText: "text-fuchsia-900 dark:text-fuchsia-100",
  },
  AP_2025_MACROECONOMICS: {
    bg: "bg-gradient-to-br from-amber-50/80 to-orange-50/60 dark:from-amber-950/25 dark:to-orange-950/15",
    activeBg:
      "bg-gradient-to-br from-amber-100/90 to-orange-100/70 dark:from-amber-900/35 dark:to-orange-900/25",
    border: "border-amber-200/50 dark:border-amber-700/40",
    activeBorder: "border-amber-400/70 dark:border-amber-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-amber-75/70 hover:to-orange-75/50 dark:hover:from-amber-900/30 dark:hover:to-orange-900/20",
    text: "text-amber-700 dark:text-amber-300",
    activeText: "text-amber-900 dark:text-amber-100",
  },
  AP_2025_MICROECONOMICS: {
    bg: "bg-gradient-to-br from-orange-50/80 to-red-50/60 dark:from-orange-950/25 dark:to-red-950/15",
    activeBg:
      "bg-gradient-to-br from-orange-100/90 to-red-100/70 dark:from-orange-900/35 dark:to-red-900/25",
    border: "border-orange-200/50 dark:border-orange-700/40",
    activeBorder: "border-orange-400/70 dark:border-orange-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-orange-75/70 hover:to-red-75/50 dark:hover:from-orange-900/30 dark:hover:to-red-900/20",
    text: "text-orange-700 dark:text-orange-300",
    activeText: "text-orange-900 dark:text-orange-100",
  },
  AP_2025_ENVIRONMENTAL_SCIENCE: {
    bg: "bg-gradient-to-br from-lime-50/80 to-green-50/60 dark:from-lime-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-lime-100/90 to-green-100/70 dark:from-lime-900/35 dark:to-green-900/25",
    border: "border-lime-200/50 dark:border-lime-700/40",
    activeBorder: "border-lime-400/70 dark:border-lime-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-lime-75/70 hover:to-green-75/50 dark:hover:from-lime-900/30 dark:hover:to-green-900/20",
    text: "text-lime-700 dark:text-lime-300",
    activeText: "text-lime-900 dark:text-lime-100",
  },
  AP_2025_EUROPEAN_HISTORY: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-blue-50/60 dark:from-indigo-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-blue-100/70 dark:from-indigo-900/35 dark:to-blue-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-blue-75/50 dark:hover:from-indigo-900/30 dark:hover:to-blue-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  AP_2025_WORLD_HISTORY_MODERN: {
    bg: "bg-gradient-to-br from-violet-50/80 to-purple-50/60 dark:from-violet-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-violet-100/90 to-purple-100/70 dark:from-violet-900/35 dark:to-purple-900/25",
    border: "border-violet-200/50 dark:border-violet-700/40",
    activeBorder: "border-violet-400/70 dark:border-violet-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-violet-75/70 hover:to-purple-75/50 dark:hover:from-violet-900/30 dark:hover:to-purple-900/20",
    text: "text-violet-700 dark:text-violet-300",
    activeText: "text-violet-900 dark:text-violet-100",
  },
  AP_2025_HUMAN_GEOGRAPHY: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  AP_2025_COMPARATIVE_GOVERNMENT_AND_POLITICS: {
    bg: "bg-gradient-to-br from-rose-50/80 to-pink-50/60 dark:from-rose-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-rose-100/90 to-pink-100/70 dark:from-rose-900/35 dark:to-pink-900/25",
    border: "border-rose-200/50 dark:border-rose-700/40",
    activeBorder: "border-rose-400/70 dark:border-rose-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-rose-75/70 hover:to-pink-75/50 dark:hover:from-rose-900/30 dark:hover:to-pink-900/20",
    text: "text-rose-700 dark:text-rose-300",
    activeText: "text-rose-900 dark:text-rose-100",
  },

  // IGCSE Course Colors - Rich, professional gradients
  IGCSE_BIOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-green-50/60 dark:from-emerald-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-green-100/70 dark:from-emerald-900/35 dark:to-green-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-green-75/50 dark:hover:from-emerald-900/30 dark:hover:to-green-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  IGCSE_CHEMISTRY: {
    bg: "bg-gradient-to-br from-blue-50/80 to-cyan-50/60 dark:from-blue-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-blue-100/90 to-cyan-100/70 dark:from-blue-900/35 dark:to-cyan-900/25",
    border: "border-blue-200/50 dark:border-blue-700/40",
    activeBorder: "border-blue-400/70 dark:border-blue-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-blue-75/70 hover:to-cyan-75/50 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/20",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  IGCSE_PHYSICS: {
    bg: "bg-gradient-to-br from-violet-50/80 to-purple-50/60 dark:from-violet-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-violet-100/90 to-purple-100/70 dark:from-violet-900/35 dark:to-purple-900/25",
    border: "border-violet-200/50 dark:border-violet-700/40",
    activeBorder: "border-violet-400/70 dark:border-violet-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-violet-75/70 hover:to-purple-75/50 dark:hover:from-violet-900/30 dark:hover:to-purple-900/20",
    text: "text-violet-700 dark:text-violet-300",
    activeText: "text-violet-900 dark:text-violet-100",
  },
  IGCSE_MATHEMATICS: {
    bg: "bg-gradient-to-br from-orange-50/80 to-amber-50/60 dark:from-orange-950/25 dark:to-amber-950/15",
    activeBg:
      "bg-gradient-to-br from-orange-100/90 to-amber-100/70 dark:from-orange-900/35 dark:to-amber-900/25",
    border: "border-orange-200/50 dark:border-orange-700/40",
    activeBorder: "border-orange-400/70 dark:border-orange-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-orange-75/70 hover:to-amber-75/50 dark:hover:from-orange-900/30 dark:hover:to-amber-900/20",
    text: "text-orange-700 dark:text-orange-300",
    activeText: "text-orange-900 dark:text-orange-100",
  },
  IGCSE_ENGLISH: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-blue-50/60 dark:from-indigo-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-blue-100/70 dark:from-indigo-900/35 dark:to-blue-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-blue-75/50 dark:hover:from-indigo-900/30 dark:hover:to-blue-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  IGCSE_LITERATURE: {
    bg: "bg-gradient-to-br from-rose-50/80 to-pink-50/60 dark:from-rose-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-rose-100/90 to-pink-100/70 dark:from-rose-900/35 dark:to-pink-900/25",
    border: "border-rose-200/50 dark:border-rose-700/40",
    activeBorder: "border-rose-400/70 dark:border-rose-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-rose-75/70 hover:to-pink-75/50 dark:hover:from-rose-900/30 dark:hover:to-pink-900/20",
    text: "text-rose-700 dark:text-rose-300",
    activeText: "text-rose-900 dark:text-rose-100",
  },
  IGCSE_HISTORY: {
    bg: "bg-gradient-to-br from-amber-50/80 to-yellow-50/60 dark:from-amber-950/25 dark:to-yellow-950/15",
    activeBg:
      "bg-gradient-to-br from-amber-100/90 to-yellow-100/70 dark:from-amber-900/35 dark:to-yellow-900/25",
    border: "border-amber-200/50 dark:border-amber-700/40",
    activeBorder: "border-amber-400/70 dark:border-amber-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-amber-75/70 hover:to-yellow-75/50 dark:hover:from-amber-900/30 dark:hover:to-yellow-900/20",
    text: "text-amber-700 dark:text-amber-300",
    activeText: "text-amber-900 dark:text-amber-100",
  },
  IGCSE_GEOGRAPHY: {
    bg: "bg-gradient-to-br from-teal-50/80 to-emerald-50/60 dark:from-teal-950/25 dark:to-emerald-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-emerald-100/70 dark:from-teal-900/35 dark:to-emerald-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-emerald-75/50 dark:hover:from-teal-900/30 dark:hover:to-emerald-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  IGCSE_ECONOMICS: {
    bg: "bg-gradient-to-br from-green-50/80 to-lime-50/60 dark:from-green-950/25 dark:to-lime-950/15",
    activeBg:
      "bg-gradient-to-br from-green-100/90 to-lime-100/70 dark:from-green-900/35 dark:to-lime-900/25",
    border: "border-green-200/50 dark:border-green-700/40",
    activeBorder: "border-green-400/70 dark:border-green-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-green-75/70 hover:to-lime-75/50 dark:hover:from-green-900/30 dark:hover:to-lime-900/20",
    text: "text-green-700 dark:text-green-300",
    activeText: "text-green-900 dark:text-green-100",
  },
  IGCSE_BUSINESS_STUDIES: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  IGCSE_ACCOUNTING: {
    bg: "bg-gradient-to-br from-zinc-50/80 to-neutral-50/60 dark:from-zinc-950/25 dark:to-neutral-950/15",
    activeBg:
      "bg-gradient-to-br from-zinc-100/90 to-neutral-100/70 dark:from-zinc-900/35 dark:to-neutral-900/25",
    border: "border-zinc-200/50 dark:border-zinc-700/40",
    activeBorder: "border-zinc-400/70 dark:border-zinc-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-zinc-75/70 hover:to-neutral-75/50 dark:hover:from-zinc-900/30 dark:hover:to-neutral-900/20",
    text: "text-zinc-700 dark:text-zinc-300",
    activeText: "text-zinc-900 dark:text-zinc-100",
  },
  IGCSE_COMPUTER_SCIENCE: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  IGCSE_ICT: {
    bg: "bg-gradient-to-br from-cyan-50/80 to-teal-50/60 dark:from-cyan-950/25 dark:to-teal-950/15",
    activeBg:
      "bg-gradient-to-br from-cyan-100/90 to-teal-100/70 dark:from-cyan-900/35 dark:to-teal-900/25",
    border: "border-cyan-200/50 dark:border-cyan-700/40",
    activeBorder: "border-cyan-400/70 dark:border-cyan-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-cyan-75/70 hover:to-teal-75/50 dark:hover:from-cyan-900/30 dark:hover:to-teal-900/20",
    text: "text-cyan-700 dark:text-cyan-300",
    activeText: "text-cyan-900 dark:text-cyan-100",
  },
  IGCSE_ART_AND_DESIGN: {
    bg: "bg-gradient-to-br from-pink-50/80 to-rose-50/60 dark:from-pink-950/25 dark:to-rose-950/15",
    activeBg:
      "bg-gradient-to-br from-pink-100/90 to-rose-100/70 dark:from-pink-900/35 dark:to-rose-900/25",
    border: "border-pink-200/50 dark:border-pink-700/40",
    activeBorder: "border-pink-400/70 dark:border-pink-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-pink-75/70 hover:to-rose-75/50 dark:hover:from-pink-900/30 dark:hover:to-rose-900/20",
    text: "text-pink-700 dark:text-pink-300",
    activeText: "text-pink-900 dark:text-pink-100",
  },
  IGCSE_MUSIC: {
    bg: "bg-gradient-to-br from-purple-50/80 to-violet-50/60 dark:from-purple-950/25 dark:to-violet-950/15",
    activeBg:
      "bg-gradient-to-br from-purple-100/90 to-violet-100/70 dark:from-purple-900/35 dark:to-violet-900/25",
    border: "border-purple-200/50 dark:border-purple-700/40",
    activeBorder: "border-purple-400/70 dark:border-purple-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-purple-75/70 hover:to-violet-75/50 dark:hover:from-purple-900/30 dark:hover:to-violet-900/20",
    text: "text-purple-700 dark:text-purple-300",
    activeText: "text-purple-900 dark:text-purple-100",
  },
  IGCSE_DRAMA: {
    bg: "bg-gradient-to-br from-fuchsia-50/80 to-purple-50/60 dark:from-fuchsia-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-fuchsia-100/90 to-purple-100/70 dark:from-fuchsia-900/35 dark:to-purple-900/25",
    border: "border-fuchsia-200/50 dark:border-fuchsia-700/40",
    activeBorder: "border-fuchsia-400/70 dark:border-fuchsia-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-fuchsia-75/70 hover:to-purple-75/50 dark:hover:from-fuchsia-900/30 dark:hover:to-purple-900/20",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
    activeText: "text-fuchsia-900 dark:text-fuchsia-100",
  },
  IGCSE_PHYSICAL_EDUCATION: {
    bg: "bg-gradient-to-br from-red-50/80 to-orange-50/60 dark:from-red-950/25 dark:to-orange-950/15",
    activeBg:
      "bg-gradient-to-br from-red-100/90 to-orange-100/70 dark:from-red-900/35 dark:to-orange-900/25",
    border: "border-red-200/50 dark:border-red-700/40",
    activeBorder: "border-red-400/70 dark:border-red-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-red-75/70 hover:to-orange-75/50 dark:hover:from-red-900/30 dark:hover:to-orange-900/20",
    text: "text-red-700 dark:text-red-300",
    activeText: "text-red-900 dark:text-red-100",
  },
  IGCSE_ENVIRONMENTAL_MANAGEMENT: {
    bg: "bg-gradient-to-br from-lime-50/80 to-green-50/60 dark:from-lime-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-lime-100/90 to-green-100/70 dark:from-lime-900/35 dark:to-green-900/25",
    border: "border-lime-200/50 dark:border-lime-700/40",
    activeBorder: "border-lime-400/70 dark:border-lime-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-lime-75/70 hover:to-green-75/50 dark:hover:from-lime-900/30 dark:hover:to-green-900/20",
    text: "text-lime-700 dark:text-lime-300",
    activeText: "text-lime-900 dark:text-lime-100",
  },
  IGCSE_PSYCHOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/25 dark:to-teal-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-teal-100/70 dark:from-emerald-900/35 dark:to-teal-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-teal-75/50 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  IGCSE_SOCIOLOGY: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-purple-50/60 dark:from-indigo-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-purple-100/70 dark:from-indigo-900/35 dark:to-purple-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-purple-75/50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },

  // IB Course Colors - Sophisticated, modern gradients
  IB_DP_BIOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-green-50/60 dark:from-emerald-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-green-100/70 dark:from-emerald-900/35 dark:to-green-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-green-75/50 dark:hover:from-emerald-900/30 dark:hover:to-green-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  IB_DP_CHEMISTRY: {
    bg: "bg-gradient-to-br from-blue-50/80 to-cyan-50/60 dark:from-blue-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-blue-100/90 to-cyan-100/70 dark:from-blue-900/35 dark:to-cyan-900/25",
    border: "border-blue-200/50 dark:border-blue-700/40",
    activeBorder: "border-blue-400/70 dark:border-blue-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-blue-75/70 hover:to-cyan-75/50 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/20",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  IB_DP_PHYSICS: {
    bg: "bg-gradient-to-br from-purple-50/80 to-violet-50/60 dark:from-purple-950/25 dark:to-violet-950/15",
    activeBg:
      "bg-gradient-to-br from-purple-100/90 to-violet-100/70 dark:from-purple-900/35 dark:to-violet-900/25",
    border: "border-purple-200/50 dark:border-purple-700/40",
    activeBorder: "border-purple-400/70 dark:border-purple-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-purple-75/70 hover:to-violet-75/50 dark:hover:from-purple-900/30 dark:hover:to-violet-900/20",
    text: "text-purple-700 dark:text-purple-300",
    activeText: "text-purple-900 dark:text-purple-100",
  },
  IB_DP_MATHEMATICS: {
    bg: "bg-gradient-to-br from-orange-50/80 to-red-50/60 dark:from-orange-950/25 dark:to-red-950/15",
    activeBg:
      "bg-gradient-to-br from-orange-100/90 to-red-100/70 dark:from-orange-900/35 dark:to-red-900/25",
    border: "border-orange-200/50 dark:border-orange-700/40",
    activeBorder: "border-orange-400/70 dark:border-orange-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-orange-75/70 hover:to-red-75/50 dark:hover:from-orange-900/30 dark:hover:to-red-900/20",
    text: "text-orange-700 dark:text-orange-300",
    activeText: "text-orange-900 dark:text-orange-100",
  },
  IB_DP_ECONOMICS: {
    bg: "bg-gradient-to-br from-teal-50/80 to-green-50/60 dark:from-teal-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-green-100/70 dark:from-teal-900/35 dark:to-green-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-green-75/50 dark:hover:from-teal-900/30 dark:hover:to-green-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  IB_DP_HISTORY: {
    bg: "bg-gradient-to-br from-amber-50/80 to-yellow-50/60 dark:from-amber-950/25 dark:to-yellow-950/15",
    activeBg:
      "bg-gradient-to-br from-amber-100/90 to-yellow-100/70 dark:from-amber-900/35 dark:to-yellow-900/25",
    border: "border-amber-200/50 dark:border-amber-700/40",
    activeBorder: "border-amber-400/70 dark:border-amber-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-amber-75/70 hover:to-yellow-75/50 dark:hover:from-amber-900/30 dark:hover:to-yellow-900/20",
    text: "text-amber-700 dark:text-amber-300",
    activeText: "text-amber-900 dark:text-amber-100",
  },
  IB_DP_COMPUTER_SCIENCE: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  IB_DP_GEOGRAPHY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-lime-50/60 dark:from-emerald-950/25 dark:to-lime-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-lime-100/70 dark:from-emerald-900/35 dark:to-lime-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-lime-75/50 dark:hover:from-emerald-900/30 dark:hover:to-lime-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  IB_DP_BUSINESS_MANAGEMENT: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },

  // A-Level Course Colors - Elegant, premium gradients
  ALEVEL_BIOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/25 dark:to-teal-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-teal-100/70 dark:from-emerald-900/35 dark:to-teal-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-teal-75/50 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  ALEVEL_CHEMISTRY: {
    bg: "bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-950/25 dark:to-indigo-950/15",
    activeBg:
      "bg-gradient-to-br from-blue-100/90 to-indigo-100/70 dark:from-blue-900/35 dark:to-indigo-900/25",
    border: "border-blue-200/50 dark:border-blue-700/40",
    activeBorder: "border-blue-400/70 dark:border-blue-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-blue-75/70 hover:to-indigo-75/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/20",
    text: "text-blue-700 dark:text-blue-300",
    activeText: "text-blue-900 dark:text-blue-100",
  },
  ALEVEL_PHYSICS: {
    bg: "bg-gradient-to-br from-purple-50/80 to-violet-50/60 dark:from-purple-950/25 dark:to-violet-950/15",
    activeBg:
      "bg-gradient-to-br from-purple-100/90 to-violet-100/70 dark:from-purple-900/35 dark:to-violet-900/25",
    border: "border-purple-200/50 dark:border-purple-700/40",
    activeBorder: "border-purple-400/70 dark:border-purple-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-purple-75/70 hover:to-violet-75/50 dark:hover:from-purple-900/30 dark:hover:to-violet-900/20",
    text: "text-purple-700 dark:text-purple-300",
    activeText: "text-purple-900 dark:text-purple-100",
  },
  ALEVEL_MATHEMATICS: {
    bg: "bg-gradient-to-br from-orange-50/80 to-amber-50/60 dark:from-orange-950/25 dark:to-amber-950/15",
    activeBg:
      "bg-gradient-to-br from-orange-100/90 to-amber-100/70 dark:from-orange-900/35 dark:to-amber-900/25",
    border: "border-orange-200/50 dark:border-orange-700/40",
    activeBorder: "border-orange-400/70 dark:border-orange-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-orange-75/70 hover:to-amber-75/50 dark:hover:from-orange-900/30 dark:hover:to-amber-900/20",
    text: "text-orange-700 dark:text-orange-300",
    activeText: "text-orange-900 dark:text-orange-100",
  },
  ALEVEL_FURTHER_MATHEMATICS: {
    bg: "bg-gradient-to-br from-red-50/80 to-rose-50/60 dark:from-red-950/25 dark:to-rose-950/15",
    activeBg:
      "bg-gradient-to-br from-red-100/90 to-rose-100/70 dark:from-red-900/35 dark:to-rose-900/25",
    border: "border-red-200/50 dark:border-red-700/40",
    activeBorder: "border-red-400/70 dark:border-red-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-red-75/70 hover:to-rose-75/50 dark:hover:from-red-900/30 dark:hover:to-rose-900/20",
    text: "text-red-700 dark:text-red-300",
    activeText: "text-red-900 dark:text-red-100",
  },
  ALEVEL_ENGLISH_LITERATURE: {
    bg: "bg-gradient-to-br from-rose-50/80 to-pink-50/60 dark:from-rose-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-rose-100/90 to-pink-100/70 dark:from-rose-900/35 dark:to-pink-900/25",
    border: "border-rose-200/50 dark:border-rose-700/40",
    activeBorder: "border-rose-400/70 dark:border-rose-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-rose-75/70 hover:to-pink-75/50 dark:hover:from-rose-900/30 dark:hover:to-pink-900/20",
    text: "text-rose-700 dark:text-rose-300",
    activeText: "text-rose-900 dark:text-rose-100",
  },
  ALEVEL_ENGLISH_LANGUAGE: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-purple-50/60 dark:from-indigo-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-purple-100/70 dark:from-indigo-900/35 dark:to-purple-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-purple-75/50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  ALEVEL_HISTORY: {
    bg: "bg-gradient-to-br from-amber-50/80 to-yellow-50/60 dark:from-amber-950/25 dark:to-yellow-950/15",
    activeBg:
      "bg-gradient-to-br from-amber-100/90 to-yellow-100/70 dark:from-amber-900/35 dark:to-yellow-900/25",
    border: "border-amber-200/50 dark:border-amber-700/40",
    activeBorder: "border-amber-400/70 dark:border-amber-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-amber-75/70 hover:to-yellow-75/50 dark:hover:from-amber-900/30 dark:hover:to-yellow-900/20",
    text: "text-amber-700 dark:text-amber-300",
    activeText: "text-amber-900 dark:text-amber-100",
  },
  ALEVEL_GEOGRAPHY: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  ALEVEL_ECONOMICS: {
    bg: "bg-gradient-to-br from-green-50/80 to-lime-50/60 dark:from-green-950/25 dark:to-lime-950/15",
    activeBg:
      "bg-gradient-to-br from-green-100/90 to-lime-100/70 dark:from-green-900/35 dark:to-lime-900/25",
    border: "border-green-200/50 dark:border-green-700/40",
    activeBorder: "border-green-400/70 dark:border-green-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-green-75/70 hover:to-lime-75/50 dark:hover:from-green-900/30 dark:hover:to-lime-900/20",
    text: "text-green-700 dark:text-green-300",
    activeText: "text-green-900 dark:text-green-100",
  },
  // ALEVEL History variants - diversify palettes to avoid repetition
  ALEVEL_EUROPEAN_HISTORY: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-blue-50/60 dark:from-indigo-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-blue-100/70 dark:from-indigo-900/35 dark:to-blue-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-blue-75/50 dark:hover:from-indigo-900/30 dark:hover:to-blue-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  ALEVEL_INTERNATIONAL_HISTORY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/25 dark:to-teal-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-teal-100/70 dark:from-emerald-900/35 dark:to-teal-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-teal-75/50 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  ALEVEL_US_HISTORY_TO_1877: {
    bg: "bg-gradient-to-br from-yellow-50/80 to-amber-50/60 dark:from-yellow-950/25 dark:to-amber-950/15",
    activeBg:
      "bg-gradient-to-br from-yellow-100/90 to-amber-100/70 dark:from-yellow-900/35 dark:to-amber-900/25",
    border: "border-yellow-200/50 dark:border-yellow-700/40",
    activeBorder: "border-yellow-400/70 dark:border-yellow-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-yellow-75/70 hover:to-amber-75/50 dark:hover:from-yellow-900/30 dark:hover:to-amber-900/20",
    text: "text-yellow-700 dark:text-yellow-300",
    activeText: "text-yellow-900 dark:text-yellow-100",
  },
  ALEVEL_US_HISTORY_SINCE_1877: {
    bg: "bg-gradient-to-br from-fuchsia-50/80 to-pink-50/60 dark:from-fuchsia-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-fuchsia-100/90 to-pink-100/70 dark:from-fuchsia-900/35 dark:to-pink-900/25",
    border: "border-fuchsia-200/50 dark:border-fuchsia-700/40",
    activeBorder: "border-fuchsia-400/70 dark:border-fuchsia-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-fuchsia-75/70 hover:to-pink-75/50 dark:hover:from-fuchsia-900/30 dark:hover:to-pink-900/20",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
    activeText: "text-fuchsia-900 dark:text-fuchsia-100",
  },
  ALEVEL_BUSINESS_STUDIES: {
    bg: "bg-gradient-to-br from-teal-50/80 to-cyan-50/60 dark:from-teal-950/25 dark:to-cyan-950/15",
    activeBg:
      "bg-gradient-to-br from-teal-100/90 to-cyan-100/70 dark:from-teal-900/35 dark:to-cyan-900/25",
    border: "border-teal-200/50 dark:border-teal-700/40",
    activeBorder: "border-teal-400/70 dark:border-teal-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-teal-75/70 hover:to-cyan-75/50 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/20",
    text: "text-teal-700 dark:text-teal-300",
    activeText: "text-teal-900 dark:text-teal-100",
  },
  ALEVEL_ACCOUNTING: {
    bg: "bg-gradient-to-br from-zinc-50/80 to-stone-50/60 dark:from-zinc-950/25 dark:to-stone-950/15",
    activeBg:
      "bg-gradient-to-br from-zinc-100/90 to-stone-100/70 dark:from-zinc-900/35 dark:to-stone-900/25",
    border: "border-zinc-200/50 dark:border-zinc-700/40",
    activeBorder: "border-zinc-400/70 dark:border-zinc-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-zinc-75/70 hover:to-stone-75/50 dark:hover:from-zinc-900/30 dark:hover:to-stone-900/20",
    text: "text-zinc-700 dark:text-zinc-300",
    activeText: "text-zinc-900 dark:text-zinc-100",
  },
  ALEVEL_COMPUTER_SCIENCE: {
    bg: "bg-gradient-to-br from-sky-50/80 to-blue-50/60 dark:from-sky-950/25 dark:to-blue-950/15",
    activeBg:
      "bg-gradient-to-br from-sky-100/90 to-blue-100/70 dark:from-sky-900/35 dark:to-blue-900/25",
    border: "border-sky-200/50 dark:border-sky-700/40",
    activeBorder: "border-sky-400/70 dark:border-sky-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-sky-75/70 hover:to-blue-75/50 dark:hover:from-sky-900/30 dark:hover:to-blue-900/20",
    text: "text-sky-700 dark:text-sky-300",
    activeText: "text-sky-900 dark:text-sky-100",
  },
  ALEVEL_ART_AND_DESIGN: {
    bg: "bg-gradient-to-br from-pink-50/80 to-fuchsia-50/60 dark:from-pink-950/25 dark:to-fuchsia-950/15",
    activeBg:
      "bg-gradient-to-br from-pink-100/90 to-fuchsia-100/70 dark:from-pink-900/35 dark:to-fuchsia-900/25",
    border: "border-pink-200/50 dark:border-pink-700/40",
    activeBorder: "border-pink-400/70 dark:border-pink-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-pink-75/70 hover:to-fuchsia-75/50 dark:hover:from-pink-900/30 dark:hover:to-fuchsia-900/20",
    text: "text-pink-700 dark:text-pink-300",
    activeText: "text-pink-900 dark:text-pink-100",
  },
  ALEVEL_MUSIC: {
    bg: "bg-gradient-to-br from-violet-50/80 to-purple-50/60 dark:from-violet-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-violet-100/90 to-purple-100/70 dark:from-violet-900/35 dark:to-purple-900/25",
    border: "border-violet-200/50 dark:border-violet-700/40",
    activeBorder: "border-violet-400/70 dark:border-violet-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-violet-75/70 hover:to-purple-75/50 dark:hover:from-violet-900/30 dark:hover:to-purple-900/20",
    text: "text-violet-700 dark:text-violet-300",
    activeText: "text-violet-900 dark:text-violet-100",
  },
  ALEVEL_DRAMA: {
    bg: "bg-gradient-to-br from-fuchsia-50/80 to-pink-50/60 dark:from-fuchsia-950/25 dark:to-pink-950/15",
    activeBg:
      "bg-gradient-to-br from-fuchsia-100/90 to-pink-100/70 dark:from-fuchsia-900/35 dark:to-pink-900/25",
    border: "border-fuchsia-200/50 dark:border-fuchsia-700/40",
    activeBorder: "border-fuchsia-400/70 dark:border-fuchsia-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-fuchsia-75/70 hover:to-pink-75/50 dark:hover:from-fuchsia-900/30 dark:hover:to-pink-900/20",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
    activeText: "text-fuchsia-900 dark:text-fuchsia-100",
  },
  ALEVEL_PSYCHOLOGY: {
    bg: "bg-gradient-to-br from-emerald-50/80 to-green-50/60 dark:from-emerald-950/25 dark:to-green-950/15",
    activeBg:
      "bg-gradient-to-br from-emerald-100/90 to-green-100/70 dark:from-emerald-900/35 dark:to-green-900/25",
    border: "border-emerald-200/50 dark:border-emerald-700/40",
    activeBorder: "border-emerald-400/70 dark:border-emerald-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-emerald-75/70 hover:to-green-75/50 dark:hover:from-emerald-900/30 dark:hover:to-green-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    activeText: "text-emerald-900 dark:text-emerald-100",
  },
  ALEVEL_SOCIOLOGY: {
    bg: "bg-gradient-to-br from-indigo-50/80 to-purple-50/60 dark:from-indigo-950/25 dark:to-purple-950/15",
    activeBg:
      "bg-gradient-to-br from-indigo-100/90 to-purple-100/70 dark:from-indigo-900/35 dark:to-purple-900/25",
    border: "border-indigo-200/50 dark:border-indigo-700/40",
    activeBorder: "border-indigo-400/70 dark:border-indigo-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-indigo-75/70 hover:to-purple-75/50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/20",
    text: "text-indigo-700 dark:text-indigo-300",
    activeText: "text-indigo-900 dark:text-indigo-100",
  },
  ALEVEL_PHILOSOPHY: {
    bg: "bg-gradient-to-br from-gray-50/80 to-slate-50/60 dark:from-gray-950/25 dark:to-slate-950/15",
    activeBg:
      "bg-gradient-to-br from-gray-100/90 to-slate-100/70 dark:from-gray-900/35 dark:to-slate-900/25",
    border: "border-gray-200/50 dark:border-gray-700/40",
    activeBorder: "border-gray-400/70 dark:border-gray-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-gray-75/70 hover:to-slate-75/50 dark:hover:from-gray-900/30 dark:hover:to-slate-900/20",
    text: "text-gray-700 dark:text-gray-300",
    activeText: "text-gray-900 dark:text-gray-100",
  },
  ALEVEL_LAW: {
    bg: "bg-gradient-to-br from-neutral-50/80 to-zinc-50/60 dark:from-neutral-950/25 dark:to-zinc-950/15",
    activeBg:
      "bg-gradient-to-br from-neutral-100/90 to-zinc-100/70 dark:from-neutral-900/35 dark:to-zinc-900/25",
    border: "border-neutral-200/50 dark:border-neutral-700/40",
    activeBorder: "border-neutral-400/70 dark:border-neutral-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-neutral-75/70 hover:to-zinc-75/50 dark:hover:from-neutral-900/30 dark:hover:to-zinc-900/20",
    text: "text-neutral-700 dark:text-neutral-300",
    activeText: "text-neutral-900 dark:text-neutral-100",
  },
  ALEVEL_POLITICS: {
    bg: "bg-gradient-to-br from-red-50/80 to-orange-50/60 dark:from-red-950/25 dark:to-orange-950/15",
    activeBg:
      "bg-gradient-to-br from-red-100/90 to-orange-100/70 dark:from-red-900/35 dark:to-orange-900/25",
    border: "border-red-200/50 dark:border-red-700/40",
    activeBorder: "border-red-400/70 dark:border-red-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-red-75/70 hover:to-orange-75/50 dark:hover:from-red-900/30 dark:hover:to-orange-900/20",
    text: "text-red-700 dark:text-red-300",
    activeText: "text-red-900 dark:text-red-100",
  },
  ALEVEL_PHYSICAL_EDUCATION: {
    bg: "bg-gradient-to-br from-yellow-50/80 to-orange-50/60 dark:from-yellow-950/25 dark:to-orange-950/15",
    activeBg:
      "bg-gradient-to-br from-yellow-100/90 to-orange-100/70 dark:from-yellow-900/35 dark:to-orange-900/25",
    border: "border-yellow-200/50 dark:border-yellow-700/40",
    activeBorder: "border-yellow-400/70 dark:border-yellow-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-yellow-75/70 hover:to-orange-75/50 dark:hover:from-yellow-900/30 dark:hover:to-orange-900/20",
    text: "text-yellow-700 dark:text-yellow-300",
    activeText: "text-yellow-900 dark:text-yellow-100",
  },
  ALEVEL_MEDIA_STUDIES: {
    bg: "bg-gradient-to-br from-cyan-50/80 to-sky-50/60 dark:from-cyan-950/25 dark:to-sky-950/15",
    activeBg:
      "bg-gradient-to-br from-cyan-100/90 to-sky-100/70 dark:from-cyan-900/35 dark:to-sky-900/25",
    border: "border-cyan-200/50 dark:border-cyan-700/40",
    activeBorder: "border-cyan-400/70 dark:border-cyan-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-cyan-75/70 hover:to-sky-75/50 dark:hover:from-cyan-900/30 dark:hover:to-sky-900/20",
    text: "text-cyan-700 dark:text-cyan-300",
    activeText: "text-cyan-900 dark:text-cyan-100",
  },
  ALEVEL_DESIGN_TECHNOLOGY: {
    bg: "bg-gradient-to-br from-lime-50/80 to-emerald-50/60 dark:from-lime-950/25 dark:to-emerald-950/15",
    activeBg:
      "bg-gradient-to-br from-lime-100/90 to-emerald-100/70 dark:from-lime-900/35 dark:to-emerald-900/25",
    border: "border-lime-200/50 dark:border-lime-700/40",
    activeBorder: "border-lime-400/70 dark:border-lime-500/60",
    hoverBg:
      "hover:bg-gradient-to-br hover:from-lime-75/70 hover:to-emerald-75/50 dark:hover:from-lime-900/30 dark:hover:to-emerald-900/20",
    text: "text-lime-700 dark:text-lime-300",
    activeText: "text-lime-900 dark:text-lime-100",
  },
};

// Group styling configurations (fallback styles only)
const groupStyles: Record<
  string,
  {
    bg: string;
    activeBg: string;
    border: string;
    activeBorder: string;
    hoverBg: string;
    text: string;
    activeText: string;
  }
> = {
  IB: {
    bg: "bg-green-50 dark:bg-slate-700",
    activeBg: "bg-green-100 dark:bg-slate-600",
    border: "border-green-200 dark:border-slate-500",
    activeBorder: "border-green-400 dark:border-green-500",
    hoverBg: "hover:bg-green-100 dark:hover:bg-slate-600",
    text: "text-green-600 dark:text-green-300",
    activeText: "text-green-800 dark:text-green-200",
  },
  IGCSE: {
    bg: "bg-purple-50 dark:bg-slate-700",
    activeBg: "bg-purple-100 dark:bg-slate-600",
    border: "border-purple-200 dark:border-slate-500",
    activeBorder: "border-purple-400 dark:border-purple-500",
    hoverBg: "hover:bg-purple-100 dark:hover:bg-slate-600",
    text: "text-purple-600 dark:text-purple-300",
    activeText: "text-purple-800 dark:text-purple-200",
  },
  ALEVEL: {
    bg: "bg-red-50 dark:bg-slate-700",
    activeBg: "bg-red-100 dark:bg-slate-600",
    border: "border-red-200 dark:border-slate-500",
    activeBorder: "border-red-400 dark:border-red-500",
    hoverBg: "hover:bg-red-100 dark:hover:bg-slate-600",
    text: "text-red-600 dark:text-red-300",
    activeText: "text-red-800 dark:text-red-200",
  },
  Other: {
    bg: "bg-gray-50 dark:bg-slate-700",
    activeBg: "bg-gray-100 dark:bg-slate-600",
    border: "border-gray-200 dark:border-slate-500",
    activeBorder: "border-gray-400 dark:border-gray-500",
    hoverBg: "hover:bg-gray-100 dark:hover:bg-slate-600",
    text: "text-gray-600 dark:text-gray-300",
    activeText: "text-gray-800 dark:text-gray-200",
  },
};

// Reactive hook to check dark mode status
const useIsDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkModeStatus = () => {
      const hasDarkClass = document.documentElement.classList.contains("dark");
      setIsDarkMode(hasDarkClass);
    };

    checkDarkModeStatus(); // Initial check

    const observer = new MutationObserver(checkDarkModeStatus);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return isDarkMode;
};

// Updated helper to extract icon hex color based on theme from combined text class string
const extractIconHexColor = (
  textClassString: string,
  isDarkMode: boolean,
): string => {
  let colorNameAndShade: string | undefined;

  if (isDarkMode) {
    const darkMatch = textClassString.match(/dark:text-([a-z]+-[0-9]+)/);
    if (darkMatch && darkMatch[1]) {
      colorNameAndShade = darkMatch[1];
    }
  }

  if (!colorNameAndShade) {
    const lightMatch = textClassString.match(/(?<!dark:)text-([a-z]+-[0-9]+)/);
    if (lightMatch && lightMatch[1]) {
      colorNameAndShade = lightMatch[1];
    } else {
      // Fallback if the specific non-dark 'text-' isn't found, try a more general 'text-'
      const generalLightMatch = textClassString.match(/text-([a-z]+-[0-9]+)/);
      if (generalLightMatch && generalLightMatch[1]) {
        colorNameAndShade = generalLightMatch[1];
      }
    }
  }

  if (!colorNameAndShade) return "currentColor";

  const [colorName, shade] = colorNameAndShade.split("-");

  // Ensure colorMap has all shades used in both light and dark themes.
  const colorMap: Record<string, Record<string, string>> = {
    blue: { "300": "#93c5fd", "700": "#1d4ed8", "900": "#1e3a8a" },
    emerald: { "300": "#6ee7b7", "700": "#047857", "900": "#064e3b" },
    amber: { "300": "#fcd34d", "700": "#b45309", "900": "#78350f" },
    green: { "300": "#86efac", "700": "#15803d", "900": "#14532d" },
    red: { "300": "#fca5a5", "700": "#c2410c", "900": "#7c2d12" },
    indigo: { "300": "#a5b4fc", "700": "#4338ca", "900": "#312e81" },
    teal: { "300": "#5eead4", "700": "#0f766e", "900": "#134e4a" },
    purple: { "300": "#d8b4fe", "700": "#7c3aed", "900": "#581c87" },
    cyan: { "300": "#67e8f9", "700": "#0e7490", "900": "#164e63" },
    yellow: { "300": "#fde047", "700": "#a16207", "900": "#713f12" },
    violet: { "300": "#c4b5fd", "700": "#6d28d9", "900": "#4c1d95" },
    pink: { "300": "#f9a8d4", "700": "#be185d", "900": "#831843" },
    fuchsia: { "300": "#f0abfc", "700": "#a21caf", "900": "#701a75" },
    orange: { "300": "#fdba74", "700": "#c2410c", "900": "#9a3412" },
    lime: { "300": "#bef264", "700": "#65a30d", "900": "#365314" },
    sky: { "300": "#7dd3fc", "700": "#0369a1", "900": "#0c4a6e" },
    rose: { "300": "#fda4af", "700": "#be123c", "900": "#881337" },
    gray: { "300": "#d1d5db", "700": "#374151", "900": "#111827" },
    slate: { "300": "#cbd5e1", "700": "#334155", "900": "#0f172a" },
    zinc: { "300": "#d4d4d8", "700": "#3f3f46", "900": "#18181b" },
    stone: { "300": "#d6d3d1", "700": "#44403c", "900": "#1c1917" },
  };

  return colorMap[colorName]?.[shade] || "currentColor";
};

// Derive a coarse hue family from a course key via its resolved style
const getHueFamilyFromKey = (key: string): string => {
  const style = getCourseStyle(key);
  const lightMatch = style.text.match(/(?<!dark:)text-([a-z]+)-[0-9]+/);
  return lightMatch?.[1] ?? "unknown";
};

// Reorder courses to avoid placing similar hues next to each other
// Tries to diversify across a grid with the given column count
const diversifyCourseOrder = (
  entries: CourseEntry[],
  columns: number = 2,
): CourseEntry[] => {
  if (entries.length <= 2) return entries;

  // Build pools grouped by hue
  const hueToEntries = new globalThis.Map<string, CourseEntry[]>();
  for (const entry of entries) {
    const [key] = entry;
    const hue = getHueFamilyFromKey(key);
    const list = hueToEntries.get(hue) ?? [];
    list.push(entry);
    hueToEntries.set(hue, list);
  }

  const result: CourseEntry[] = [];
  const total = entries.length;

  const pickHue = (disallowed: Set<string>): string | undefined => {
    // Choose the hue with the largest remaining pool that's not disallowed
    let bestHue: string | undefined;
    let bestCount = -1;
    for (const [hue, list] of hueToEntries.entries()) {
      if (list.length === 0) continue;
      const count = list.length;
      if (!disallowed.has(hue) && count > bestCount) {
        bestHue = hue;
        bestCount = count;
      }
    }
    if (bestHue) return bestHue;
    // Fallback: pick any remaining hue
    for (const [hue, list] of hueToEntries.entries()) {
      if (list.length > 0) return hue;
    }
    return undefined;
  };

  for (let i = 0; i < total; i += 1) {
    const disallowed = new Set<string>();
    // Disallow same hue horizontally
    if (i % columns !== 0) {
      const left = result[i - 1];
      if (left) disallowed.add(getHueFamilyFromKey(left[0]));
    }
    // Disallow same hue vertically
    if (i - columns >= 0) {
      const up = result[i - columns];
      if (up) disallowed.add(getHueFamilyFromKey(up[0]));
    }

    const hue = pickHue(disallowed);
    if (!hue) break;
    const list = hueToEntries.get(hue)!;
    const picked = list.shift()!;
    result.push(picked);
  }

  // If anything remains (edge cases), append them
  for (const list of hueToEntries.values()) {
    while (list.length) result.push(list.shift()!);
  }

  return result;
};

// Get course icon component
const getCourseIcon = (key: string): IconComponent => {
  if (courseIcons[key]) {
    return courseIcons[key];
  }

  // Check if it belongs to a course group
  if (key.startsWith("AP_2025_")) {
    // For any unmapped AP_2025 courses, return default AP icon
    return GraduationCap;
  } else if (key.startsWith("IB_")) {
    return groupIcons.IB;
  } else if (key.startsWith("IGCSE_")) {
    return groupIcons.IGCSE;
  } else if (key.startsWith("ALEVEL_")) {
    return groupIcons.ALEVEL;
  }

  // Fallback to default icon
  return courseIcons.DEFAULT;
};

// Normalize variant keys to base subject keys so all variants share same palette
const normalizeCourseKey = (key: string): string => {
  // Biology
  if (/(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?BIOLOGY$/.test(key)) {
    return "BIOLOGY";
  }

  // Physics
  if (/(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?PHYSICS/.test(key)) {
    return "PHYSICS";
  }

  // Computer Science
  if (/(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?COMPUTER_SCIENCE/.test(key)) {
    return "COMPUTER_SCIENCE";
  }

  // Business Studies / Business Management
  if (
    /(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?BUSINESS_STUDIES$/.test(key) ||
    /(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?BUSINESS_MANAGEMENT$/.test(key)
  ) {
    return "BUSINESS_STUDIES";
  }

  // Sociology
  if (/(AP_2025_|IB_DP_|IGCSE_|ALEVEL_)?SOCIOLOGY$/.test(key)) {
    return "SOCIOLOGY";
  }

  return key;
};

// Get color styling for a course - unified by base subjects first
const getCourseStyle = (key: string) => {
  // PRIORITY 1: normalized base subject color
  const normalized = normalizeCourseKey(key);
  if (courseColors.hasOwnProperty(normalized)) {
    return courseColors[normalized];
  }

  // PRIORITY 2: exact course key mapping if defined
  if (courseColors.hasOwnProperty(key)) {
    return courseColors[key];
  }

  // PRIORITY 3: Only use group styles as absolute fallback for unmapped courses
  if (key.startsWith("AP_2025_")) {
    // For unmapped AP courses, use a default AP style
    return {
      bg: "bg-gray-50 dark:bg-slate-700",
      activeBg: "bg-gray-100 dark:bg-slate-600",
      border: "border-gray-200 dark:border-slate-500",
      activeBorder: "border-gray-400 dark:border-gray-500",
      hoverBg: "hover:bg-gray-100 dark:hover:bg-slate-600",
      text: "text-gray-600 dark:text-gray-300",
      activeText: "text-gray-800 dark:text-gray-200",
    };
  } else if (key.startsWith("IB_")) {
    // Only use group style if no specific color is defined in courseColors
    return groupStyles.IB;
  } else if (key.startsWith("IGCSE_")) {
    return groupStyles.IGCSE;
  } else if (key.startsWith("ALEVEL_")) {
    return groupStyles.ALEVEL;
  }

  // Fallback to "Other" style
  return groupStyles.Other;
};

// Define the set of custom icons for the check
const customIconComponents = new Set<IconComponent>([
  BiologyIcon,
  GeographyIcon,
  LiteratureIcon,
  PhilosophyIcon,
  PhysicsIcon,
  ChemistryIcon,
  MathematicsIcon,
  HistoryIcon,
]);

// Render icon based on its type
const renderCourseIcon = (Icon: IconComponent, color: string) => {
  // Check if it's a custom icon (BiologyIcon, etc.) or a Lucide icon
  const isCustomIcon = customIconComponents.has(Icon);

  if (isCustomIcon) {
    return <Icon width="20" height="20" className="mr-2" color={color} />;
  }

  // It's a Lucide icon - all have consistent size
  return <Icon size={20} className="mr-2" color={color} />;
};

const EnglishCourseSelector = ({
  selectedGrade,
  selectedCourse,
  setSelectedCourse,
  visibleCourses,
}: {
  selectedGrade: number;
  selectedCourse: GetTopicsCourseType;
  setSelectedCourse: (course: GetTopicsCourseType) => void;
  visibleCourses: CourseEntry[];
}) => {
  const { t } = useTranslation();
  const isDarkMode = useIsDarkMode();

  return (
    <>
      {diversifyCourseOrder(visibleCourses, 2).map(([key, value]) => {
        const courseStyle = getCourseStyle(key);
        const CourseIcon = getCourseIcon(key);
        const iconColor =
          selectedCourse === key
            ? extractIconHexColor(courseStyle.activeText, isDarkMode)
            : extractIconHexColor(courseStyle.text, isDarkMode);

        return (
          <div
            key={key}
            data-testid="course-selector-button"
            data-course={key}
            className={`cursor-pointer gap-2 rounded-lg text-sm p-3 border transition-colors duration-200 font-medium flex items-center ${
              selectedCourse === key
                ? `${courseStyle.activeBg} ${courseStyle.activeBorder} ${courseStyle.activeText}`
                : `${courseStyle.bg} ${courseStyle.border} ${courseStyle.text} ${courseStyle.hoverBg}`
            }`}
            onClick={() => {
              setSelectedCourse(key as GetTopicsCourseType);
            }}
          >
            {renderCourseIcon(CourseIcon, iconColor)}
            <div>{t("courseType." + value.name(selectedGrade))}</div>
          </div>
        );
      })}
    </>
  );
};

const CourseSelector = ({
  selectedGrade,
  selectedCourse,
  setSelectedCourse,
  filteredCourses,
}: {
  selectedGrade: number;
  selectedCourse: GetTopicsCourseType;
  setSelectedCourse: (course: GetTopicsCourseType) => void;
  filteredCourses?: GetTopicsCourseType[];
}) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";
  const isDarkMode = useIsDarkMode();

  // Get all visible courses
  const visibleCourses = Object.entries(CourseData)
    .filter(([, value]) => value.visible(selectedGrade ?? 5))
    .filter(([key]) => !filteredCourses?.includes(key as GetTopicsCourseType));

  // For English language, use the English selector
  if (isEnglish) {
    return (
      <EnglishCourseSelector
        selectedGrade={selectedGrade}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        visibleCourses={visibleCourses}
      />
    );
  }

  // For non-English languages, use the modified rendering with divs
  return (
    <>
      {diversifyCourseOrder(visibleCourses, 2).map(([key, value]) => {
        const courseStyle = getCourseStyle(key);
        const CourseIcon = getCourseIcon(key);
        const iconColor =
          selectedCourse === key
            ? extractIconHexColor(courseStyle.activeText, isDarkMode)
            : extractIconHexColor(courseStyle.text, isDarkMode);

        return (
          <div
            key={key}
            data-testid="course-selector-button"
            data-course={key}
            className={`cursor-pointer gap-2 rounded-lg text-sm p-3 border transition-colors duration-200 font-medium flex items-center ${
              selectedCourse === key
                ? `border-2 ${courseStyle.activeBg} ${courseStyle.activeBorder} ${courseStyle.activeText}`
                : ` ${courseStyle.bg} ${courseStyle.border} ${courseStyle.text} ${courseStyle.hoverBg}`
            }`}
            onClick={() => {
              setSelectedCourse(key as GetTopicsCourseType);
            }}
          >
            {renderCourseIcon(CourseIcon, iconColor)}
            <div>{t("courseType." + value.name(selectedGrade ?? 5))}</div>
          </div>
        );
      })}
    </>
  );
};

export default CourseSelector;
